/**
 * Partner Portal — read-only Territory view (Slice A)
 *
 * Franchisees can view their admin-controlled territory here but
 * cannot self-edit any value — territory is owned by the admin per
 * the Feb-2026-July onboarding brief. Any changes are requested via
 * a contact link to the AWARE team.
 */
import { useEffect, useState } from "react";
import { MapPin, ShieldCheck, Mail, Info, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { PartnerShell } from "./Dashboard";

const STATUS_TONE = {
  draft:           "bg-slate-100 text-slate-700 border-slate-200",
  confirmed:       "bg-emerald-50 text-emerald-800 border-emerald-200",
  review_required: "bg-amber-50 text-amber-800 border-amber-200",
};
const STATUS_LABEL = {
  draft:           "Draft",
  confirmed:       "Confirmed",
  review_required: "Review required",
};

export default function PartnerTerritory() {
  useSEO({ title: "My Territory — AWARE Partner Portal", noindex: true, includeBaseline: false });
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/partner/territory")
      .then(({ data }) => setT(data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load territory"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <PartnerShell>
        <div className="p-8 flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading territory…
        </div>
      </PartnerShell>
    );
  }
  if (error) {
    return (
      <PartnerShell>
        <div className="p-8 text-sm text-rose-700">{String(error)}</div>
      </PartnerShell>
    );
  }

  // Feb 2026 — wrap in PartnerShell so the partner nav/header renders
  // on this page too. Prior to this fix the page rendered as a bare
  // <div> and the whole partner portal chrome disappeared on click,
  // making it look like the partner had been logged out.
  return (
    <PartnerShell>
      <div className="max-w-3xl mx-auto p-6" data-testid="partner-territory-page">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">
        Partner Portal
      </div>
      <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">My territory</h1>
      <p className="mt-3 text-sm text-slate-600 max-w-xl">
        Your service area is set by the AWARE admin team. To request a change, email us — see the bottom of this page.
      </p>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 space-y-5" data-testid="partner-territory-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <ShieldCheck className="h-4 w-4 text-cyan-700" /> Territory status
          </div>
          <span
            data-testid="partner-territory-status"
            className={`text-xs px-2.5 py-1 rounded-full border font-medium ${STATUS_TONE[t.territory_status] || STATUS_TONE.draft}`}
          >
            {STATUS_LABEL[t.territory_status] || t.territory_status}
          </span>
        </div>

        <Row label="Partner type"      value={t.partner_type || "—"} />
        <Row label="Franchise / region" value={t.franchise_region || "—"} />
        <Row label="Service state"      value={t.service_state || "—"} />
        <Row label="Mail-out capable"   value={t.mail_out_capable ? "Yes" : "No"} />

        <div>
          <div className="text-xs text-slate-500 font-medium mb-1.5 flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" /> Service postcodes ({(t.service_postcodes || []).length})
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="partner-territory-postcodes">
            {(t.service_postcodes || []).length === 0 ? (
              <span className="text-xs text-slate-400">No postcodes assigned yet.</span>
            ) : t.service_postcodes.map((pc) => (
              <span key={pc} className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-800 font-mono">{pc}</span>
            ))}
          </div>
        </div>

        {(t.service_suburbs || []).length > 0 && (
          <div>
            <div className="text-xs text-slate-500 font-medium mb-1.5">Service suburbs</div>
            <div className="text-sm text-slate-700" data-testid="partner-territory-suburbs">
              {t.service_suburbs.join(", ")}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 flex items-start gap-2">
        <Info className="h-4 w-4 flex-none text-cyan-700 mt-0.5" />
        <div>
          <b>Need a territory change?</b> Postcodes and suburbs are managed centrally to keep partner areas fair.
          <br />
          <a href="mailto:admin@awarewatertesting.com.au?subject=Territory%20change%20request"
             className="mt-1 inline-flex items-center gap-1 text-cyan-700 hover:underline text-sm font-medium">
            <Mail className="h-3.5 w-3.5" /> Email the AWARE team
          </a>
        </div>
      </div>
      </div>
    </PartnerShell>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5 border-b border-slate-100 last:border-0">
      <div className="text-xs text-slate-500 min-w-[140px]">{label}</div>
      <div className="text-sm text-slate-800">{value}</div>
    </div>
  );
}
